import type {
  EndCustomer,
  ReferencePoint,
  RouteCalculationResponse,
} from "@/interfaces";
import { parseOSRMRoute } from "@/lib/polyline";
import ReferencePointMarker from "@/pages/Routing/ReferencePointMarker";
import L from "leaflet";
import { useEffect, useRef } from "react";
import { Marker, Tooltip, useMap } from "react-leaflet";

interface InteractiveMapContentProps {
  customers: EndCustomer[];
  referencePoints: ReferencePoint[];
  selectedCustomers: number[];
  selectedReferencePoint?: number;
  onCustomerSelect: (customerId: number) => void;
  onReferencePointSelect: (referencePointId: number) => void;
  routeData?: RouteCalculationResponse | null;
}

const getOptimizedCustomerOrder = (
  routeData: RouteCalculationResponse,
  selectedCustomers: number[],
  customers: EndCustomer[],
): EndCustomer[] => {
  if (!routeData?.osrm_route?.waypoints) {
    return customers.filter((customer) =>
      selectedCustomers.includes(customer.id),
    );
  }

  const waypoints = routeData.osrm_route.waypoints;

  const customerWaypoints = waypoints.slice(1, -1);

  const orderedCustomers: EndCustomer[] = [];

  customerWaypoints.forEach((waypoint) => {
    const [lng, lat] = waypoint.location;

    let closestCustomer: EndCustomer | null = null;
    let minDistance = Number.POSITIVE_INFINITY;

    customers.forEach((customer) => {
      if (!selectedCustomers.includes(customer.id)) return;
      if (orderedCustomers.find((c) => c.id === customer.id)) return;

      const customerLat = Number(customer.latitude);
      const customerLng = Number(customer.longitude);
      if (isNaN(customerLat) || isNaN(customerLng)) return;

      const distance = Math.sqrt(
        Math.pow(lat - customerLat, 2) + Math.pow(lng - customerLng, 2),
      );

      const addressMatch =
        customer.address &&
        waypoint.name &&
        (customer.address.toLowerCase().includes(waypoint.name.toLowerCase()) ||
          waypoint.name.toLowerCase().includes(customer.address.toLowerCase()));

      if (addressMatch || distance < minDistance) {
        minDistance = distance;
        closestCustomer = customer;
      }
    });

    if (closestCustomer) {
      orderedCustomers.push(closestCustomer);
    }
  });

  selectedCustomers.forEach((customerId) => {
    if (!orderedCustomers.find((c) => c.id === customerId)) {
      const customer = customers.find((c) => c.id === customerId);
      if (customer) {
        orderedCustomers.push(customer);
      }
    }
  });

  return orderedCustomers;
};

const InteractiveMapContent: React.FC<InteractiveMapContentProps> = ({
  customers,
  referencePoints,
  selectedCustomers,
  selectedReferencePoint,
  onCustomerSelect,
  onReferencePointSelect,
  routeData,
}) => {
  const map = useMap();
  const layersRef = useRef<L.LayerGroup | null>(null);
  const routeLayerRef = useRef<L.Polyline | null>(null);

  useEffect(() => {
    if (!map) return;

    if (!layersRef.current) {
      const layerGroup = L.layerGroup();
      layersRef.current = layerGroup;
      layerGroup.addTo(map);
    }

    return () => {
      if (layersRef.current) {
        map.removeLayer(layersRef.current);
        layersRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!layersRef.current) return;

    if (routeLayerRef.current) {
      layersRef.current.removeLayer(routeLayerRef.current);
      routeLayerRef.current = null;
    }

    if (routeData?.osrm_route) {
      const parsedRoute = parseOSRMRoute(routeData.osrm_route);
      if (parsedRoute) {
        const routeLine = L.polyline(parsedRoute.coordinates, {
          color: "#2563EB",
          weight: 5,
          opacity: 0.8,
          dashArray: "0",
        });
        layersRef.current.addLayer(routeLine);
        routeLayerRef.current = routeLine;
      }
    }
  }, [routeData]);

  const validCustomers = customers.filter((customer) => {
    const lat = customer.latitude ? Number(customer.latitude) : null;
    const lng = customer.longitude ? Number(customer.longitude) : null;
    return lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng);
  });

  const orderedCustomers = routeData
    ? getOptimizedCustomerOrder(routeData, selectedCustomers, customers)
    : customers.filter((customer) => selectedCustomers.includes(customer.id));

  const createCustomerIcon = (isSelected: boolean, optimizedIndex: number) => {
    const color = isSelected ? "#ef4444" : "#6b7280";
    const size = isSelected ? 30 : 24;

    return L.divIcon({
      html: `
        <div style="
          width: ${size}px;
          height: ${size}px;
          background-color: ${color};
          border: 2px solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          color: white;
          font-size: ${size > 24 ? "12px" : "10px"};
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">
          ${optimizedIndex >= 0 ? optimizedIndex + 1 : ""}
        </div>
      `,
      className: "custom-customer-icon",
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  };

  if (!layersRef.current) {
    return null;
  }

  return (
    <>
      {validCustomers.map((customer) => {
        const isSelected = selectedCustomers.includes(customer.id);
        const optimizedIndex =
          routeData && isSelected
            ? orderedCustomers.findIndex((c) => c.id === customer.id)
            : -1;

        const lat = Number(customer.latitude);
        const lng = Number(customer.longitude);

        return (
          <Marker
            key={customer.id}
            position={[lat, lng]}
            icon={createCustomerIcon(isSelected, optimizedIndex)}
            eventHandlers={{
              click: () => {
                if (!routeData) {
                  onCustomerSelect(customer.id);
                }
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -15]} opacity={0.9}>
              <div className="text-sm font-medium">
                {customer.name || `Cliente ${customer.id}`}
                {customer.address && (
                  <div className="text-xs text-gray-600 mt-1">
                    {customer.address}
                  </div>
                )}
              </div>
            </Tooltip>
          </Marker>
        );
      })}
      {referencePoints
        .filter((rp) => rp.is_headquarters)
        .map((referencePoint) => {
          const isSelected = selectedReferencePoint === referencePoint.id;

          return (
            <ReferencePointMarker
              key={referencePoint.id}
              referencePoint={referencePoint}
              isSelected={isSelected}
              isInteractionDisabled={!!routeData}
              onReferencePointSelect={onReferencePointSelect}
              layerGroup={layersRef.current!}
            />
          );
        })}
    </>
  );
};

export default InteractiveMapContent;
